import type { SourcePackFileResponse } from '@malaberg/shared';
/**
 * SourcePackFilePicker — T27.
 *
 * Multi-select of source-pack files with drag-to-reorder (arrow-button a11y).
 * Fetches `sourcePackFilesApi.list()` on mount.
 * Renders:
 *   1. A MultiSelect for choosing files
 *   2. An ordered list of selected files with ↑/↓ arrow buttons for reordering
 *
 * Each selected item carries `{ id, version }` and its displayOrder is its
 * array index (0-based).
 */
import { ActionIcon, Box, Group, Loader, MultiSelect, Stack, Text } from '@mantine/core';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { sourcePackFilesApi } from '../../api/endpoints/source-pack-files';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SelectedSourcePackFile {
  id: string;
  version: number;
}

interface Props {
  /** Currently selected files, in display order. */
  value: SelectedSourcePackFile[];
  onChange: (files: SelectedSourcePackFile[]) => void;
  /**
   * The product these files will attach to. Used to group the MultiSelect
   * options into "Core / This product / Other products" so users don't
   * accidentally ship another product's brief to this one's prompt.
   */
  productId?: string | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SourcePackFilePicker({ value, onChange, productId = null }: Props) {
  const [availableFiles, setAvailableFiles] = useState<SourcePackFileResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The productId that the current `availableFiles` corresponds to. Until
  // this matches the prop, the sync-selection effect must not run — otherwise
  // it would auto-select files from the *previous* productId.
  const [fetchedProductId, setFetchedProductId] = useState<string | null>(null);

  // Fetch source-pack files filtered to this product's scope. Server returns
  // rows where scope = 'workspace' OR scope = 'product:<productId>'. When no
  // productId is provided (e.g. editing a brand-new product before the id has
  // been typed), we fall back to workspace-only — the operator can re-open
  // after setting the id to pick up product-scoped files.
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const query = productId ? { productId } : {};
        const result = (await sourcePackFilesApi.list(query)) as {
          ok: boolean;
          files?: SourcePackFileResponse[];
          items?: SourcePackFileResponse[];
        };
        if (!cancelled) {
          setAvailableFiles(result.files ?? result.items ?? []);
          setFetchedProductId(productId ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load source-pack files');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // Re-fetch when productId changes so the picker only ever offers files
    // that are scope='workspace' OR scope='product:<this productId>'.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // ── Categorise by scope (authoritative) ───────────────────────────────────
  // The API filters out other-products' files server-side; we just split the
  // returned set into "workspace" vs "this product" so the picker groups them.
  type Category = 'workspace' | 'this-product';
  function categorise(file: SourcePackFileResponse): Category {
    if (file.scope === 'workspace') return 'workspace';
    if (productId && file.scope === `product:${productId}`) return 'this-product';
    return 'workspace'; // fallback (shouldn't happen given the server filter)
  }

  const grouped: Record<Category, { value: string; label: string }[]> = {
    workspace: [],
    'this-product': [],
  };
  for (const f of availableFiles) {
    grouped[categorise(f)].push({ value: f.id, label: f.filename });
  }

  const selectOptions: Array<{ group: string; items: { value: string; label: string }[] }> = [];
  if (grouped.workspace.length)
    selectOptions.push({ group: 'Workspace (every product)', items: grouped.workspace });
  if (grouped['this-product'].length)
    selectOptions.push({ group: 'This product only', items: grouped['this-product'] });

  // ── Sync selected files with the scope-filtered list ──────────────────────
  //   - When productId changes (incl. first non-empty value), replace the
  //     selection with the full set of relevant files (server returns
  //     workspace + product:<productId>).
  //   - Prune any out-of-scope selections that slipped through.
  //   - Skip while loading or before a productId is supplied (don't dump the
  //     entire 14-file workspace into a brand-new product).
  //   - CRITICAL: gate on `fetchedProductId === productId` so we don't auto-
  //     select using files left over from the previous productId. Without
  //     this gate, switching products would select the old product's files,
  //     and the subsequent refetch would prune them down to just workspace.
  const lastProductIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (loading || availableFiles.length === 0) return;
    if (!productId) return;
    if (fetchedProductId !== productId) return;

    const availableIds = new Set(availableFiles.map((f) => f.id));

    if (lastProductIdRef.current !== productId) {
      // productId just changed — set the selection to "all relevant".
      const initial = availableFiles.map((f) => ({ id: f.id, version: f.version }));
      onChange(initial);
      lastProductIdRef.current = productId;
      return;
    }

    // Same productId — prune stale selections only.
    const pruned = value.filter((f) => availableIds.has(f.id));
    if (pruned.length !== value.length) {
      onChange(pruned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, availableFiles.length, productId, fetchedProductId]);

  // The selected IDs (preserving order)
  const selectedIds = value.map((f) => f.id);

  function handleMultiSelectChange(ids: string[]) {
    // Build the new selection list:
    // - Keep existing selected files in their current order if still present
    // - Append newly added files at the end
    const existing = value.filter((f) => ids.includes(f.id));
    const existingIds = new Set(existing.map((f) => f.id));
    const added = ids
      .filter((id) => !existingIds.has(id))
      .map((id) => {
        const file = availableFiles.find((f) => f.id === id);
        return { id, version: file?.version ?? 1 };
      });
    onChange([...existing, ...added]);
  }

  function moveItem(index: number, direction: 'up' | 'down') {
    const next = [...value];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= next.length) return;
    const temp = next[index];
    next[index] = next[targetIndex];
    next[targetIndex] = temp;
    onChange(next);
  }

  function getFilename(id: string): string {
    return availableFiles.find((f) => f.id === id)?.filename ?? id;
  }

  if (loading) {
    return (
      <Box data-testid="source-pack-picker">
        <Loader size="sm" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box data-testid="source-pack-picker">
        <Text c="red" size="sm">
          {error}
        </Text>
      </Box>
    );
  }

  return (
    <Stack data-testid="source-pack-picker" gap="xs">
      <MultiSelect
        data-testid="source-pack-multiselect"
        label="Source-pack files"
        description="Core files are pre-selected. Add only the product-specific files relevant to this product."
        placeholder={selectOptions.length === 0 ? 'No files available' : 'Select files…'}
        data={selectOptions}
        value={selectedIds}
        onChange={handleMultiSelectChange}
        searchable
        clearable
      />

      {/* Ordered list of selected files with reorder buttons */}
      {value.length > 0 && (
        <Stack gap={4}>
          {value.map((file, index) => (
            <Group key={file.id} data-testid={`spf-item-${file.id}`} gap="xs" align="center">
              <Text size="sm" flex={1}>
                {index + 1}. {getFilename(file.id)}
              </Text>
              <ActionIcon
                data-testid={`spf-move-up-${file.id}`}
                size="sm"
                variant="subtle"
                aria-label={`Move ${getFilename(file.id)} up`}
                disabled={index === 0}
                onClick={() => moveItem(index, 'up')}
              >
                <IconArrowUp size={14} />
              </ActionIcon>
              <ActionIcon
                data-testid={`spf-move-down-${file.id}`}
                size="sm"
                variant="subtle"
                aria-label={`Move ${getFilename(file.id)} down`}
                disabled={index === value.length - 1}
                onClick={() => moveItem(index, 'down')}
              >
                <IconArrowDown size={14} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
